import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PageTitle from './PageTitle'
import './brand/brand.css'
import './LandingPreview.css'
import BrandHeader from './brand/BrandHeader'
import BrandFooter from './brand/BrandFooter'
import { useBrandHead } from './brand/useBrandHead'
import { sendEmailFunc } from '../App'
import { subscribeToNewsletter } from '../utils/newsletter'
import { stores, pickStore } from '../data/amazonStores'
import homeBack from '../assets/home_back.jpg'
import reviewPhoto1 from '../assets/landing-preview/review-photo-1.jpg'
import reviewPhoto2 from '../assets/landing-preview/review-photo-2.jpg'
import reviewPhoto3 from '../assets/landing-preview/review-photo-3.jpg'
import fingerPaint from '../assets/home-preview/finger-paint.jpg'
import paintPens from '../assets/home-preview/paint-pens.jpg'
import ceramicPaint from '../assets/home-preview/ceramic-paint.jpg'
import fabricPaint from '../assets/home-preview/fabric-paint.jpg'
import geckoPencils from '../assets/home-preview/gecko-pencils.jpg'
import candlePens from '../assets/home-preview/candle-pens.jpg'

// Internal test version of the proposed /landing redesign (English only, noindex).
// Gift for a social media post, not for an Amazon review. The form really sends
// emails (subject marked [TEST]) and writes newsletter opt-ins with source `landing-preview`.

// Placeholder until the team picks the real code.
const DISCOUNT_CODE = 'COLOZOO10'
const MIN_FOLLOWERS = 100
const KEEP_POST_DAYS = 30
const HANDLE = '@colozoo.official'
const INSTAGRAM_URL = 'https://www.instagram.com/colozoo.official'
const FACEBOOK_URL = 'https://www.facebook.com/Colozoo.Official'

const showcase = [
  { name: 'Finger Paint', image: fingerPaint },
  { name: 'Paint Pens', image: paintPens },
  { name: 'Glass & Porcelain Paint', image: ceramicPaint },
  { name: 'Fabric Paint', image: fabricPaint },
  { name: '3-in-1 Gecko Pencils', image: geckoPencils },
  { name: 'Candle Pens', image: candlePens },
]

// Customer creations with their words (from their Amazon reviews).
const creations = [
  {
    name: 'Nokcha',
    product: 'Porcelain pens',
    text: 'Simply fantastic! They wipe off easily with a damp cloth, and the colours mix wonderfully.',
    photo: reviewPhoto1,
  },
  {
    name: 'Julia.Katharina',
    product: '3-in-1 Gecko Pencils',
    text: 'Great pens for my 4 year old son. Feels great in the hand and paints on black paper too.',
    photo: reviewPhoto2,
  },
  {
    name: 'Katharina',
    product: 'Fabric Paint',
    text: 'Really opaque colours. We made handprints, and it survives the wash after ironing.',
    photo: reviewPhoto3,
  },
]

const COUNTRIES = 'Austria, Belgium, Bulgaria, Croatia, Cyprus, Czech Republic, Denmark, Estonia, Finland, France, ' +
  'Germany, Greece, Hungary, Ireland, Italy, Latvia, Lithuania, Luxembourg, Malta, Netherlands, Poland, Portugal, ' +
  'Romania, Slovakia, Slovenia, Spain, Sweden, USA'

const rules = [
  `Post a photo or video of something you made with Colozoo on Instagram or Facebook and tag ${HANDLE}.`,
  'It has to be a permanent post or Reel. Stories and other disappearing posts don’t count.',
  `Your account is public and active, with at least ${MIN_FOLLOWERS} followers.`,
  `Keep the post online for at least ${KEEP_POST_DAYS} days.`,
  'You bought a Colozoo product on Amazon and give us the order number.',
  'One product up to €25 that is in stock in your Amazon store — or several small ones up to €15 in total.',
  'One gift per customer.',
  'We may repost your photo on our channels, always with credit to you.',
]

const faq = [
  {
    q: 'Do I have to leave an Amazon review?',
    a: 'No. The gift is for your post. An honest review helps a small brand like ours a lot, but it is never required.',
  },
  {
    q: 'Which social networks count?',
    a: `Instagram and Facebook — a feed post or a Reel that tags ${HANDLE}. Stories don’t count because they disappear.`,
  },
  {
    q: 'My account is private.',
    a: 'Please make the post public, otherwise we can’t see it.',
  },
  {
    q: 'I don’t want to show my artwork.',
    a: 'A photo of the product in use is perfect too.',
  },
  {
    q: 'Can I post in my own language?',
    a: 'Yes, any language is fine.',
  },
  {
    q: 'Can I pick several products?',
    a: 'One product up to €25, or several small ones up to €15 in total, because shipping costs more.',
  },
  {
    q: 'Can I pick something over €25 and pay the difference?',
    a: 'Sorry, no — the gift has to be €25 or less.',
  },
  {
    q: 'The product I want is out of stock.',
    a: 'Please pick something that is in stock in your country’s Amazon store.',
  },
  {
    q: 'When will my gift arrive?',
    a: 'We check your post and ship within about a week. If nothing arrives in 10 days, write to support@colozoo.de.',
  },
  {
    q: 'How do I know you got my form?',
    a: 'You get a confirmation email right away. No email? Check your spam folder or write to support@colozoo.de.',
  },
  {
    q: 'I bought another product. Can I get another gift?',
    a: 'We love seeing more of your creations, but the gift is once per customer.',
  },
]

interface Claim {
  postLink: string
  gift: string
  orderNumber: string
  email: string
  name: string
  street: string
  postalCode: string
  city: string
  country: string
  newsletter: boolean
}

const emptyClaim: Claim = {
  postLink: '',
  gift: '',
  orderNumber: '',
  email: '',
  name: '',
  street: '',
  postalCode: '',
  city: '',
  country: '',
  newsletter: false,
}

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const customerEmail = (claim: Claim, storeUrl: string) => `
<div style="font-family:Arial,sans-serif;font-size:16px;line-height:1.5;color:#231f20;max-width:560px">
  <h2 style="color:#da2128">Thank you — we’ve got your gift request!</h2>
  <p>We’ll check your post and ship <strong>${escapeHtml(claim.gift)}</strong> within about a week.</p>
  <p>Here is your thank-you code for <strong>10% off</strong> your next Colozoo order:</p>
  <p style="font-size:24px;font-weight:bold;letter-spacing:2px;border:2px dashed #ffcf3f;
    display:inline-block;padding:8px 16px">${DISCOUNT_CODE}</p>
  <p>Enjoying your Colozoo set? An honest review on <a href="${storeUrl}">Amazon</a> helps a small family brand a lot.
  It’s completely optional and has nothing to do with your gift.</p>
  <p>Questions? Just reply to support@colozoo.de.</p>
  <p>Happy creating!<br/>Your Colozoo team</p>
</div>`

const adminEmail = (claim: Claim, language: string) => [
  ['Post link', claim.postLink],
  ['Gift', claim.gift],
  ['Amazon order number', claim.orderNumber],
  ['Email', claim.email],
  ['Different address', claim.street ? `${claim.name}, ${claim.street}, ${claim.postalCode} ${claim.city}, ${claim.country}` : 'no'],
  ['Newsletter', claim.newsletter ? 'yes' : 'no'],
  ['Language', language],
].map(([ k, v ]) => `${k}: ${escapeHtml(v)}`).join('<br />')

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const CopyButton = ({ code }: { code: string }) => {
  const [ copied, setCopied ] = useState(false)
  const copy = () => {
    navigator.clipboard?.writeText(code).catch(() => undefined)
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }
  return (
    <button type="button" className="lp-code" onClick={copy}>
      <span className="lp-code-value">{code}</span>
      <span className="lp-code-action">{copied ? 'Copied!' : 'Copy'}</span>
    </button>
  )
}

const CreationsCarousel = () => {
  const track = useRef<HTMLDivElement>(null)
  const [ active, setActive ] = useState(0)

  const go = (i: number) => {
    const el = track.current
    if (!el) return
    const next = (i + creations.length) % creations.length
    el.scrollTo({ left: next * el.clientWidth, behavior: 'smooth' })
  }

  const onScroll = () => {
    const el = track.current
    if (el) setActive(Math.round(el.scrollLeft / el.clientWidth))
  }

  return (
    <div className="lp-carousel">
      <div className="lp-carousel-track" ref={track} onScroll={onScroll}>
        {creations.map((c) => (
          <figure key={c.name} className="lp-review">
            <img src={c.photo} alt={`Made by ${c.name} with Colozoo ${c.product}`} className="lp-review-photo" loading="lazy" />
            <div className="lp-review-body">
              <span className="lp-stars" aria-label="5 out of 5 stars">★★★★★</span>
              <strong className="lp-review-title">{c.product}</strong>
              <p>“{c.text}”</p>
              <figcaption>Made by {c.name}</figcaption>
            </div>
          </figure>
        ))}
      </div>
      <div className="lp-carousel-nav">
        <button type="button" aria-label="Previous" onClick={() => go(active - 1)}>‹</button>
        <span className="lp-dots">
          {creations.map((c, i) => (
            <button
              key={c.name}
              type="button"
              aria-label={`Creation ${i + 1}`}
              className={i === active ? 'lp-dot-on' : ''}
              onClick={() => go(i)}
            />
          ))}
        </span>
        <button type="button" aria-label="Next" onClick={() => go(active + 1)}>›</button>
      </div>
    </div>
  )
}

const LandingPreview = () => {
  const { i18n } = useTranslation()
  const [ store, setStore ] = useState(() => pickStore(i18n.language))
  const [ claim, setClaim ] = useState<Claim>(emptyClaim)
  const [ otherAddress, setOtherAddress ] = useState(false)
  const [ status, setStatus ] = useState<'idle' | 'sending' | 'done' | 'error'>('idle')

  useBrandHead({ noindex: true })

  const field = (key: keyof Claim) => ({
    value: claim[key] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setClaim({ ...claim, [key]: e.target.value }),
  })

  const onClaim = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('sending')
    const language = i18n.language || 'en-GB'
    const sent = otherAddress ? claim : { ...claim, name: '', street: '', postalCode: '', city: '', country: '' }
    try {
      await sendEmailFunc(JSON.stringify({
        email: 'support@colozoo.de',
        subject: '[TEST /landing_preview] New gift claim (social post)',
        message: adminEmail(sent, language),
      }))
      await sendEmailFunc(JSON.stringify({
        email: sent.email,
        subject: 'Your Colozoo gift + 10% thank-you code',
        message: customerEmail(sent, store.url),
      }))
      if (sent.newsletter) {
        try {
          await subscribeToNewsletter({ email: sent.email, language, source: 'landing-preview' })
        } catch (error) {
          console.error('Could not add user to newsletter:', error)
        }
      }
      setStatus('done')
      scrollTo('claim')
    } catch (error) {
      console.error('Failed to send gift claim:', error)
      setStatus('error')
    }
  }

  return (
    <>
      <PageTitle title="Colozoo — Free gift landing preview" />
      <div className="czb-page lp">
        <BrandHeader
          logoHref="#top"
          nav={[
            { href: '#how', label: 'How it works' },
            { href: '#rules', label: 'Gift rules' },
            { href: '#faq', label: 'Questions' },
          ]}
          cta={{ href: '#claim', label: 'Get my free gift' }}
          onLanguageChange={(lng) => setStore(pickStore(lng))}
        />

        {/* Hero on the home page jungle background */}
        <section id="top" className="lp-hero czb-jungle">
          <img src={homeBack} alt="" aria-hidden="true" className="czb-jungle-art" />
          <div className="lp-wrap lp-hero-inner">
            <div className="lp-hero-copy">
              <p className="lp-eyebrow">Thank you for choosing Colozoo</p>
              <h1>Show us what you made.<br />Pick a free gift.</h1>
              <p className="lp-lead">
                Post a photo of your Colozoo creation, tag {HANDLE} and choose any product up to €25. We ship it free.
              </p>
              <button type="button" className="czb-btn czb-btn-red" onClick={() => scrollTo('how')}>
                Get my free gift
              </button>
            </div>
          </div>
        </section>

        {/* Steps */}
        <section id="how" className="lp-steps">
          <div className="lp-wrap">
            <ol className="lp-steps-row">
              <li><span className="lp-step-num">1</span><strong>Post your creation</strong><span>Tag {HANDLE}</span></li>
              <li><span className="lp-step-num">2</span><strong>Pick your gift</strong><span>Anything up to €25 in our store</span></li>
              <li><span className="lp-step-num">3</span><strong>Send the form</strong><span>We ship it free</span></li>
            </ol>
          </div>
        </section>

        {/* Step 1: post a creation */}
        <section className="lp-section lp-mist">
          <div className="lp-wrap lp-review-grid">
            <div className="lp-section-head lp-left">
              <p className="lp-eyebrow lp-eyebrow-dark">Step 1</p>
              <h2>Post what you made</h2>
              <p>
                Take a photo or video of your artwork — or just the set in use. Post it on{' '}
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">Instagram</a> or{' '}
                <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer">Facebook</a> and tag <strong>{HANDLE}</strong>.
              </p>
              <p className="lp-small">A feed post or Reel, not a Story — Stories disappear and we can’t check them.</p>
            </div>
            <CreationsCarousel />
          </div>
        </section>

        {/* Step 2: pick a gift in the Amazon store */}
        <section className="lp-section">
          <div className="lp-wrap">
            <div className="lp-section-head">
              <p className="lp-eyebrow lp-eyebrow-dark">Step 2</p>
              <h2>Pick anything up to €25</h2>
              <p>Browse our Amazon store and choose any product that is in stock.</p>
            </div>
            <div className="lp-showcase" aria-hidden="true">
              {showcase.map((p) => (
                <div key={p.name} className="lp-showcase-item">
                  <img src={p.image} alt="" loading="lazy" />
                  <span>{p.name}</span>
                </div>
              ))}
            </div>
            <div className="lp-store">
              <a href={store.url} target="_blank" rel="noopener noreferrer" className="czb-btn czb-btn-red">
                Open our store on {store.domain} ↗
              </a>
              <div className="lp-chips" role="group" aria-label="Choose your Amazon store">
                {stores.map((s) => (
                  <button
                    key={s.code}
                    type="button"
                    className={s === store ? 'lp-chip lp-chip-on' : 'lp-chip'}
                    onClick={() => setStore(s)}
                  >
                    {s.code}
                  </button>
                ))}
              </div>
              <p className="lp-small">Copy the product name or link — you’ll paste it in the form.</p>
            </div>
          </div>
        </section>

        {/* Step 3: claim form + rules */}
        <section id="claim" className="lp-claim czb-jungle">
          <img src={homeBack} alt="" aria-hidden="true" className="czb-jungle-art czb-jungle-art-top" />
          <div className="lp-wrap lp-claim-grid">
            {status === 'done' ? (
              <div className="lp-card lp-success">
                <span className="lp-success-icon" aria-hidden="true">🎉</span>
                <h2>Thank you — we’ve got it!</h2>
                <p>We’ll check your post and ship your gift within about a week. A confirmation email is on its way.</p>
                <p><strong>Our thank-you:</strong> 10% off your next Colozoo order.</p>
                <CopyButton code={DISCOUNT_CODE} />
                <div className="lp-review-ask">
                  <p>Enjoying your set? An honest Amazon review helps a small family brand a lot. Completely optional.</p>
                  <a href={store.url} target="_blank" rel="noopener noreferrer">Go to our Amazon store ↗</a>
                </div>
              </div>
            ) : (
              <form className="lp-card lp-form" onSubmit={onClaim}>
                <div className="lp-section-head lp-left">
                  <p className="lp-eyebrow lp-eyebrow-dark">Step 3</p>
                  <h2>Claim your gift</h2>
                </div>

                <label className="lp-field">
                  <span>Link to your post</span>
                  <input type="url" required placeholder="https://www.instagram.com/p/…" {...field('postLink')} />
                </label>
                <label className="lp-field">
                  <span>Your gift (product name or Amazon link)</span>
                  <input required placeholder="e.g. Colozoo Fabric Paint, 14 colours" {...field('gift')} />
                </label>
                <div className="lp-field-row">
                  <label className="lp-field">
                    <span>Amazon order number</span>
                    <input
                      required
                      pattern="\d{3}-\d{7}-\d{7}"
                      title="Format: 123-4567890-1234567"
                      placeholder="123-4567890-1234567"
                      {...field('orderNumber')}
                    />
                  </label>
                  <label className="lp-field">
                    <span>Email</span>
                    <input type="email" required placeholder="you@example.com" {...field('email')} />
                  </label>
                </div>

                <label className="lp-check">
                  <input type="checkbox" checked={otherAddress} onChange={(e) => setOtherAddress(e.target.checked)} />
                  <span>Ship to a different address than my Amazon order</span>
                </label>
                {otherAddress && (
                  <div className="lp-address">
                    <input required placeholder="Full name" {...field('name')} />
                    <input required placeholder="Street and house number" {...field('street')} />
                    <div className="lp-field-row">
                      <input required placeholder="Postal code" {...field('postalCode')} />
                      <input required placeholder="City" {...field('city')} />
                    </div>
                    <input required placeholder="Country" {...field('country')} />
                  </div>
                )}

                <label className="lp-check">
                  <input type="checkbox" required />
                  <span>
                    I accept the gift rules and the{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
                  </span>
                </label>
                <label className="lp-check">
                  <input
                    type="checkbox"
                    checked={claim.newsletter}
                    onChange={(e) => setClaim({ ...claim, newsletter: e.target.checked })}
                  />
                  <span>Send me news about new Colozoo products and offers. No spam.</span>
                </label>

                {status === 'error' && (
                  <p className="lp-error">Something went wrong. Please try again or write to support@colozoo.de.</p>
                )}
                <button type="submit" className="czb-btn czb-btn-red czb-btn-wide" disabled={status === 'sending'}>
                  {status === 'sending' ? 'Sending…' : 'Get my free gift'}
                </button>
              </form>
            )}

            <aside id="rules" className="lp-card lp-rules">
              <h3>Gift rules</h3>
              <ol>
                {rules.map((r) => <li key={r}>{r}</li>)}
              </ol>
              <div className="lp-countries">
                <strong>🌍 Open to customers in the EU and the USA:</strong>
                <p>{COUNTRIES}.</p>
              </div>
            </aside>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="lp-section">
          <div className="lp-wrap">
            <div className="lp-section-head">
              <h2>Questions</h2>
            </div>
            {/* Two independent columns, so opening a question only grows its own column */}
            <div className="lp-faq">
              {[ faq.slice(0, Math.ceil(faq.length / 2)), faq.slice(Math.ceil(faq.length / 2)) ].map((column, i) => (
                <div key={i}>
                  {column.map((item) => (
                    <details key={item.q}>
                      <summary>{item.q}</summary>
                      <p>{item.a}</p>
                    </details>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>

        <BrandFooter />

        {status !== 'done' && (
          <div className="lp-sticky">
            <button type="button" className="czb-btn czb-btn-red czb-btn-wide" onClick={() => scrollTo('claim')}>
              Get my free gift
            </button>
          </div>
        )}
      </div>
    </>
  )
}

export default LandingPreview
